import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { CheckCircle, AlertTriangle, Clock, FileWarning, ArrowRight, Shield } from 'lucide-react';

const VoluntaryDisclosures = () => {
  return (
    <div className="service-page">
      <Header />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Voluntary<br />Disclosure Services
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8">
              Everyone makes mistakes. Proactively address trade compliance violations before they trigger costly audits. Expert guidance through the voluntary disclosure process with BIS, DDTC, and CBP.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-slate-700 hover:bg-slate-800 text-white px-10 h-14">
                Discuss Your Situation Confidentially
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why VSD */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Get Ahead of Errors Before They Trigger an Audit
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                When trade compliance violations are discovered voluntarily and disclosed promptly to government authorities, companies often receive significantly reduced penalties or avoid sanctions entirely. Delayed disclosure or discovery by authorities during an audit dramatically increases financial and operational consequences.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our voluntary disclosure services help you navigate this sensitive process strategically, minimizing liability while demonstrating good faith compliance efforts to regulators.
              </p>
            </div>
            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-8">
              <AlertTriangle className="h-12 w-12 text-yellow-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">Timing is Critical</h3>
              <div className="space-y-4 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Voluntary Disclosure:</p>
                  <p>Potential for reduced penalties, no denial of privileges, demonstrates compliance culture</p>
                </div>
                <div>
                  <p className="font-semibold mb-1">After Government Discovery:</p>
                  <p>Full penalties, possible denial of export privileges, criminal referral in willful cases</p>
                </div>
                <div className="mt-4 pt-4 border-t border-yellow-200">
                  <Clock className="h-6 w-6 text-yellow-600 mb-2" />
                  <p className="font-semibold">The window for voluntary disclosure closes once government agencies initiate investigation or audit activities.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">BIS Voluntary Self-Disclosure</h3>
              <p className="text-gray-600 text-sm mb-4">Export Administration Regulations violations</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Unauthorized exports of controlled items</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Deemed export violations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Misclassification or license exception errors</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Denied party screening failures</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">DDTC Voluntary Disclosure</h3>
              <p className="text-gray-600 text-sm mb-4">ITAR violations and defense trade infractions</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Unauthorized USML exports</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Technical data disclosure violations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Registration or reporting failures</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Foreign person access control breaches</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">CBP Prior Disclosure</h3>
              <p className="text-gray-600 text-sm mb-4">Import and customs law violations</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Misclassification or valuation errors</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Country of origin misstatements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>FTA qualification errors</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Quota or trade remedy violations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12">
            Our Voluntary Disclosure Process
          </h2>

          <div className="space-y-6">
            {[
              {
                num: 1,
                title: "Confidential Assessment & Investigation",
                desc: "Comprehensive review of the suspected violation(s) under attorney-client privilege. Determination of scope, root causes, and potential exposure. Assessment of whether voluntary disclosure is the optimal strategy."
              },
              {
                num: 2,
                title: "Corrective Action Implementation",
                desc: "Development and implementation of immediate remedial measures to prevent recurrence. Documentation of corrective actions taken demonstrates commitment to compliance and mitigates penalty exposure."
              },
              {
                num: 3,
                title: "Disclosure Preparation & Strategy",
                desc: "Preparation of detailed narrative disclosure including violation description, root cause analysis, and corrective actions. Strategic presentation to position the company favorably and minimize sanctions."
              },
              {
                num: 4,
                title: "Submission & Agency Engagement",
                desc: "Filing of voluntary disclosure with appropriate government agency. Ongoing engagement with enforcement personnel throughout the review process. Negotiation of settlement terms and penalty mitigation."
              },
              {
                num: 5,
                title: "Resolution & Compliance Enhancement",
                desc: "Closure of the voluntary disclosure matter with formalized settlement or warning letter. Enhancement of compliance programs to address identified weaknesses and prevent future violations."
              }
            ].map((step) => (
              <div key={step.num} className="flex gap-6">
                <div className="w-12 h-12 bg-slate-700 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {step.num}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12">
            Why Choose Our VSD Services
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <Shield className="h-8 w-8 text-slate-700 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Penalty Mitigation Experience</h3>
                <p className="text-gray-600">Extensive experience negotiating with BIS, DDTC, and CBP enforcement personnel. Track record of achieving favorable settlement terms and penalty reductions.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <Shield className="h-8 w-8 text-slate-700 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Strategic Approach</h3>
                <p className="text-gray-600">Not all discovered violations warrant voluntary disclosure. We provide strategic counsel on when disclosure serves your interests and when alternative approaches may be preferable.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <Shield className="h-8 w-8 text-slate-700 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Attorney-Client Privilege</h3>
                <p className="text-gray-600">Initial assessments conducted under privilege protect sensitive information. Only information necessary for disclosure is ultimately shared with government authorities.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <Shield className="h-8 w-8 text-slate-700 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Compliance Program Enhancement</h3>
                <p className="text-gray-600">Transform enforcement matters into opportunities to strengthen your compliance program and demonstrate organizational commitment to regulatory adherence.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Time-Sensitive Compliance Matter?
          </h2>
          <p className="text-base sm:text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Contact us immediately for confidential assessment of your voluntary disclosure options.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Request Confidential Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VoluntaryDisclosures;